import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Table, TableBody } from '@/components/ui/catalyst/table';
import { useListArtifactsQuery } from '@/services/api/artifactService';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { ArtifactRow } from './ArtifactRow';
import { ArtifactBulkActions } from './components/ArtifactBulkActions';
import { ArtifactEmptyState } from './components/ArtifactEmptyState';
import { ArtifactListHeader } from './components/ArtifactListHeader';
import { ArtifactLoadingState } from './components/ArtifactLoadingState';
import { useArtifactFiltering } from './hooks/useArtifactFiltering';
import { useArtifactNavigation } from './hooks/useArtifactNavigation';
import { useArtifactSelection } from './hooks/useArtifactSelection';

interface ArtifactListPageProps {
  matterId: string;
}

export const ArtifactListPage = ({ matterId }: ArtifactListPageProps) => {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<
    ArtifactType | 'unprocessed' | 'all'
  >('all');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const navigate = useNavigate();

  const {
    data: artifacts,
    isLoading,
    isError,
    refetch,
  } = useListArtifactsQuery(
    { matterId },
    {
      skip: !matterId,
    },
  );

  // Use custom hooks
  const { processedArtifacts, stats, availableTypes } = useArtifactFiltering({
    artifacts,
    searchQuery,
    typeFilter,
    sortOrder,
  });

  const { selectedIndex } = useArtifactNavigation({
    artifacts: processedArtifacts,
    onSelect: (artifact) => navigate(`${artifact.id}`),
  });

  const {
    selectedIds,
    selectedCount,
    selectArtifact,
    deselectArtifact,
    toggleArtifact,
    selectAll,
    clearSelection,
    isSelected,
    getSelectedArtifacts,
  } = useArtifactSelection();

  const handleToggleSort = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
  };

  const handleSelectionChange = (id: string, checked: boolean) => {
    if (checked) {
      selectArtifact(id);
    } else {
      deselectArtifact(id);
    }
  };

  const handleToggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      clearSelection();
    }
  };

  // Calculate total count for display
  const totalCount =
    artifacts?.filter(
      (a) => a.artifact_type !== ArtifactType.AUDIO_CONVERSATION,
    ).length || 0;

  // Loading state
  if (isLoading) {
    return <ArtifactLoadingState />;
  }

  // Error state
  if (isError || !artifacts) {
    return <ArtifactEmptyState variant="error" onRetry={() => refetch()} />;
  }

  // No artifacts state
  if (artifacts.length === 0) {
    return <ArtifactEmptyState variant="no-artifacts" />;
  }

  // No search results state
  if (
    processedArtifacts.length === 0 &&
    (searchQuery || typeFilter !== 'all')
  ) {
    return (
      <div className="h-full w-full flex flex-col">
        <ArtifactListHeader
          totalCount={totalCount}
          filteredCount={processedArtifacts.length}
          sortOrder={sortOrder}
          onToggleSort={handleToggleSort}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          availableTypes={availableTypes}
          stats={stats}
          onClearFilters={handleClearFilters}
          isSelectionMode={isSelectionMode}
          onToggleSelectionMode={handleToggleSelectionMode}
          selectedCount={selectedCount}
        />
        <ArtifactEmptyState
          variant="no-results"
          onClearFilters={handleClearFilters}
          searchQuery={searchQuery}
          typeFilter={typeFilter}
        />
      </div>
    );
  }

  // Main list view
  return (
    <div className="h-full w-full flex flex-col">
      <ArtifactListHeader
        totalCount={totalCount}
        filteredCount={processedArtifacts.length}
        sortOrder={sortOrder}
        onToggleSort={handleToggleSort}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        availableTypes={availableTypes}
        stats={stats}
        onClearFilters={handleClearFilters}
        isSelectionMode={isSelectionMode}
        onToggleSelectionMode={handleToggleSelectionMode}
        selectedCount={selectedCount}
      />

      {/* Artifact list */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto px-4 pt-1 pb-4">
          <Table className="[--gutter:--spacing(6)] sm:[--gutter:--spacing(8)]">
            <TableBody>
              {processedArtifacts.map((artifact, index) => (
                <ArtifactRow
                  key={artifact.id}
                  artifact={artifact}
                  isSelected={index === selectedIndex}
                  isSelectable={isSelectionMode}
                  isChecked={isSelected(artifact.id)}
                  onSelectionChange={handleSelectionChange}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Bulk actions */}
      <ArtifactBulkActions
        selectedArtifacts={getSelectedArtifacts(processedArtifacts)}
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        matterId={matterId}
      />
    </div>
  );
};
